// ─── Una data scritta in italiano, e un calendarietto che resta ──────────────
//
// 🗣️ 17/09/2026, sua: *«Sì, la data scrivila tu in italiano»* — guardando un
// «Fino al» che diceva `04/22/2027`.
//
// 🚨 **Quel formato non era una nostra scelta sbagliata: non era una nostra
// scelta.** Un `<input type="date">` lo disegna il browser con la SUA lingua, e
// non lo governano né `lang`, né la lingua della pagina, né niente che stia in
// questo repository. ⇒ L'unico modo di scrivere la data in italiano è
// **smettere di farla scrivere a lui**.
//
// ⚖️ **Ma il calendarietto non si butta via per questo.** È lì che si sceglie
// un giorno senza contare le settimane, e su un telefono è l'unico modo civile.
// ⇒ Il campo diventa nostro — testo, `gg/mm/aaaa`, letto e scritto da noi — e
// il campo data del browser resta **sotto**, invisibile, al servizio del
// bottoncino che apre il calendario. È la stessa forma già scelta il 16/09 per
// la barra del giorno (⇒ `GiornoScelto`), qui applicata a tutti gli altri campi.
//
// 📌 *Riprendersi un pezzo di interfaccia non vuol dire rinunciare a ciò che
//    faceva: vuol dire tenerlo, e disegnarci sopra.*

import { useRef, useState } from 'react';
import { daItaliano, inItaliano } from '../lib/time.js';

/**
 * ⚠️ **`showPicker()` non si dà per scontato** (Safari lo ha dalla 16). Dove non
 * c'è, il bottoncino non compare e resta il campo scritto: si batte la data a
 * mano, che è meno comodo ma non è una funzione persa.
 * 🚨 E qui c'è **una** definizione, non due: `GiornoScelto` legge questa.
 */
export const SA_APRIRE_IL_CALENDARIETTO =
  typeof HTMLInputElement !== 'undefined' && 'showPicker' in HTMLInputElement.prototype;

export function CampoData({
  etichetta,
  value,
  onChange,
  min,
  max,
  ...resto
}: {
  /** Con l'etichetta il campo si veste da `Campo`; senza, sta dove lo si mette. */
  etichetta?: string;
  /** Sempre ISO — `2027-04-22` — o vuoto. Il formato italiano non esce di qui. */
  value: string;
  onChange(iso: string): void;
  min?: string;
  max?: string;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'min' | 'max'
>): React.JSX.Element {
  const sotto = useRef<HTMLInputElement>(null);
  const [testo, setTesto] = useState(() => inItaliano(value));
  /*
   * ⭐ Si tiene da parte l'ultimo valore VISTO da fuori, e si risincronizza
   * durante il disegno invece che con un effetto: così una data cambiata dal di
   * fuori (il calendarietto, un ripristino) arriva nel campo senza un secondo
   * giro di disegno in cui il campo dice ancora la cosa vecchia.
   */
  const [visto, setVisto] = useState(value);
  if (value !== visto) {
    setVisto(value);
    setTesto(inItaliano(value));
  }

  /** Mentre si scrive si accetta solo l'anno per esteso (⇒ `daItaliano`). */
  function scrivi(grezzo: string): void {
    setTesto(grezzo);
    const iso = daItaliano(grezzo);
    if (iso !== null) {
      setVisto(iso);
      onChange(iso);
    }
  }

  /*
   * ⭐ All'uscita dal campo si fa i conti: quello che è una data si normalizza a
   * `gg/mm/aaaa` (e qui l'anno corto vale), quello che non lo è **torna com'era**.
   * ⛔ Lasciare a schermo una scritta che non è una data farebbe credere che sia
   * stata presa: il campo direbbe una cosa e la prenotazione ne saprebbe
   * un'altra.
   */
  function esci(): void {
    /*
     * ⭐ Il campo svuotato vuol dire **svuotato**, e non «riscrivici quello di
     * prima»: ci sono caselle in cui vuoto è una risposta buona — il «fino a»
     * della cassa, l'«al» di una chiusura di un giorno solo. Farla tornare
     * piena sarebbe un campo che non si lascia cancellare.
     */
    if (testo.trim() === '') {
      if (visto !== '') {
        setVisto('');
        onChange('');
      }
      return;
    }
    const iso = daItaliano(testo, true);
    if (iso === null) {
      setTesto(inItaliano(visto));
      return;
    }
    setTesto(inItaliano(iso));
    if (iso !== visto) {
      setVisto(iso);
      onChange(iso);
    }
  }

  /*
   * 🚨 **È un `div` e non uno `span`, e non è indifferente.** `.campo > span` è
   * la regola che veste l'ETICHETTA di un campo — blocco, piccola, tenue — e
   * dentro un `<label className="campo">` avrebbe vestito così anche questo
   * involucro, vincendo per specificità sulla sua classe. 📏 Visto a schermo: il
   * bottoncino del calendario finiva **sotto** al campo invece che accanto.
   * 📌 *Un selettore scritto per l'unico figlio che c'era diventa una trappola
   *    il giorno in cui i figli sono due.*
   */
  const campo = (
    <div className="campo-data">
      <input
        {...resto}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="gg/mm/aaaa"
        value={testo}
        onChange={(e) => scrivi(e.target.value)}
        onBlur={esci}
      />
      {SA_APRIRE_IL_CALENDARIETTO && (
        <>
          <button
            type="button"
            className="apri-calendario"
            title="Scegli sul calendario"
            aria-label="Scegli sul calendario"
            disabled={resto.disabled}
            onClick={() => {
              try {
                sotto.current?.showPicker();
              } catch {
                /* Il browser può rifiutare (fuori da un gesto, campo non
                   raggiungibile): allora non si resta bloccati — si batte. */
                sotto.current?.focus();
              }
            }}
          >
            {/*
              ⚠️ **Disegnato, non scritto.** Un'emoji dipende dai caratteri
              installati: 📏 su questo banco `🗓` esce come un quadratino vuoto, e
              un bottone con dentro un quadratino non dice cosa fa. Un disegno
              non ha questo problema da nessuna parte.
            */}
            <svg width="15" height="16" viewBox="0 0 15 16" aria-hidden="true" focusable="false">
              <rect
                x="0.75"
                y="2.75"
                width="13.5"
                height="12.5"
                rx="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path d="M0.75 6.5h13.5M4.5 0.75v3M10.5 0.75v3" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
          {/* Il campo vero resta nel documento: è lui che il calendarietto apre,
              ed è lui che dice al browser dove disegnarlo. Fuori dal giro del
              Tab, perché il comando è il campo scritto qui sopra. */}
          <input
            ref={sotto}
            type="date"
            className="data-sotto"
            value={value}
            min={min}
            max={max}
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => e.target.value && onChange(e.target.value)}
          />
        </>
      )}
    </div>
  );

  if (etichetta === undefined) return campo;
  return (
    <label className="campo">
      <span>{etichetta}</span>
      {campo}
    </label>
  );
}
